import React, { useEffect, useState } from 'react';
import { Edit, FileCopy } from '@material-ui/icons';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Box, Button, Grid, IconButton } from '@material-ui/core';
import TableView from '../TableView/TableView';
import {
  getEquipmentHoldingProfile,
  getEquipmentHoldingProfiles,
} from '../../redux/actions/equipmentsActions';
import EquipmentDeleteHoldingProfilesButton from './EquipmentDeleteHoldingProfilesButton';
import { removeEntity } from '../../redux/actions/equipmentHoldingProfileActions';
import { snackbarShowMessage } from '../../redux/actions/snackbarActions';
import { VIEW_MODE_STATES } from './utils';
import {
  cleanDefaultString,
  wrapEquipmentAvailabilities,
} from '../../utils/helpers';
import { useTranslation } from 'react-i18next';
import store from '../../redux/store';

function EquipmentHoldingProfileList({
  searchFilters,
  setViewMode,
  setAutocomleteSelectors,
  setCopyMessage,
}) {
  const [page, setPage] = useState(1);
  const { t } = useTranslation();
  const columns = [
    {
      id: 'availability',
      label: t('app.common.availability'),
      align: 'left',
      minWidth: 50,
    },
    {
      id: 'category',
      label: t('equipment.category'),
      align: 'left',
      minWidth: 50,
    },
    {
      id: 'manufacturer',
      label: t('equipment.supplier'),
      align: 'left',
      minWidth: 50,
    },
    {
      id: 'model',
      label: t('equipment.model'),
      align: 'left',
      minWidth: 50,
    },
    {
      id: 'actions',
      label: t('app.common.actions'),
      align: 'center',
      minWidth: 50,
    },
  ];

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const dispatch: any = useDispatch();
  const { selectedBrand } = useSelector(
    (state: RootStateOrAny) => state.UIReducer,
  );
  const { equipmentHoldingProfiles } = useSelector(
    (state: RootStateOrAny) => state.EquipmentsReducer,
  );

  const [treatedHoldingProfiles, setTreatedHoldingProfiles] = useState([]);

  const handleAddNewProfile = async () => {
    setViewMode(VIEW_MODE_STATES.CREATE);
  };

  useEffect(() => {
    dispatch(
      getEquipmentHoldingProfiles({
        brand: selectedBrand,
        page,
        limit: rowsPerPage,
        ...wrapEquipmentAvailabilities(
          cleanDefaultString(searchFilters, 'all'),
        ),
      }),
    );
  }, [dispatch, page, rowsPerPage, selectedBrand]);

  const updateAutoCompleteSelector = values => {
    if (!values?.data) {
      return;
    }
    const { holdingProfile } = values.data;
    if (holdingProfile) {
      const { category, manufacturer, model } = holdingProfile;
      const modelId = model.replace(/(?!\w|\s)./g, '');
      setAutocomleteSelectors(prevState => ({
        ...prevState,
        category,
        manufacturer,
        model: { id: modelId, name: model },
      }));
    }
  };

  const handleEdit = id => () => {
    setCopyMessage(false);
    setViewMode(VIEW_MODE_STATES.EDIT);
    dispatch(getEquipmentHoldingProfile({ brand: selectedBrand }, id)).then(
      response => updateAutoCompleteSelector(response),
    );
  };

  const handleClone = id => () => {
    setCopyMessage(true);
    setViewMode(VIEW_MODE_STATES.CLONE);
    dispatch(getEquipmentHoldingProfile({ brand: selectedBrand }, id)).then(
      response => updateAutoCompleteSelector(response),
    );
  };
  const handleDelete = holdingProfile => async () => {
    const response: any = await dispatch(removeEntity(holdingProfile));
    if (response.status < 300) {
      dispatch(snackbarShowMessage(response.data.message));
    }
    dispatch(
      getEquipmentHoldingProfiles({
        brand: selectedBrand,
        page,
        limit: rowsPerPage,
        ...wrapEquipmentAvailabilities({
          ...searchFilters,
          region: '',
          country: '',
          state: '',
        }),
      }),
    );
  };

  const getAvailabilitiToShow = availabilities => {
    if (!(availabilities && Array.isArray(availabilities)))
      return availabilities;

    let regions: string[] = [];
    let countries: string[] = [];
    let states: string[] = [];
    const done = [];
    availabilities.forEach(profile => {
      if (!regions.includes(profile.region)) regions.push(profile.region);
      else if (
        profile?.country?.description &&
        !countries.includes(profile.country.description)
      )
        countries.push(profile?.country.description);
      else if (
        profile?.state?.description &&
        !states.includes(profile.state.description)
      )
        states.push(profile?.state.description);
    });

    regions = regions.sort((regionA, regionB) => {
      if (regionA === 'GLOBAL') return -1;
      if (regionB === 'GLOBAL') return 1;
      return regionA > regionB ? 1 : regionA < regionB ? -1 : 0;
    });
    countries = countries.sort((A, B) => (A > B ? 1 : A < B ? -1 : 0));
    states = states.sort((A, B) => (A > B ? 1 : A < B ? -1 : 0));

    const toShow = [...regions, ...countries, ...states].slice(0, 3);
    return ` ${toShow.join(',')}${toShow.length > 2 ? '...' : ''}`;
  };

  useEffect(() => {
    if (equipmentHoldingProfiles.holdingProfiles?.length) {
      setTreatedHoldingProfiles(
        equipmentHoldingProfiles?.holdingProfiles?.map(holdingProfile => {
          const newHoldingProfile: any = {};
          newHoldingProfile.id = holdingProfile.id;
          newHoldingProfile.availability = getAvailabilitiToShow(
            holdingProfile?.availabilities,
          );
          newHoldingProfile.manufacturer = holdingProfile?.manufacturer?.name;
          newHoldingProfile.model = holdingProfile.model;
          newHoldingProfile.category = holdingProfile.category.name;
          newHoldingProfile.actions = (
            <>
              <IconButton onClick={handleClone(newHoldingProfile.id)}>
                <FileCopy />
              </IconButton>
              <IconButton onClick={handleEdit(newHoldingProfile.id)}>
                <Edit />
              </IconButton>
              <IconButton>
                <EquipmentDeleteHoldingProfilesButton
                  holdingProfile={holdingProfile}
                  handleDelete={handleDelete}
                />
              </IconButton>
            </>
          );
          return newHoldingProfile;
        }),
      );
    } else {
      setTreatedHoldingProfiles([]);
    }
  }, [equipmentHoldingProfiles]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
    dispatch(
      getEquipmentHoldingProfiles({
        brand: selectedBrand,
        page: 1,
        limit: +event.target.value,
        ...wrapEquipmentAvailabilities(
          cleanDefaultString(searchFilters, 'all'),
        ),
      }),
    );
  };

  const handleClickSearchButton = () => {
    dispatch(
      getEquipmentHoldingProfiles({
        brand: selectedBrand,
        page: 1,
        limit: rowsPerPage,
        ...wrapEquipmentAvailabilities(
          cleanDefaultString(searchFilters, 'all'),
        ),
      }),
    );
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
    dispatch(
      getEquipmentHoldingProfiles({
        brand: selectedBrand,
        page: newPage + 1,
        limit: rowsPerPage,
        ...wrapEquipmentAvailabilities(
          cleanDefaultString(searchFilters, 'all'),
        ),
      }),
    );
  };

  return (
    <>
      <TableView
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        columns={columns}
        rows={treatedHoldingProfiles}
        messageEmptyArray={t('NoDataLegend')}
        paginationConfig={{
          rowsPerPage: rowsPerPage,
          page: page,
          total: equipmentHoldingProfiles?.pagination?.total || 0,
        }}
      />
      <Grid container>
        <Grid item>
          <Box ml={0} m={1}>
            <Button
              variant="contained"
              color="default"
              disabled={
                !searchFilters.category &&
                !searchFilters.manufacturer &&
                !searchFilters.model
              }
              onClick={handleClickSearchButton}
            >
              Search
            </Button>
          </Box>
        </Grid>
        <Grid item>
          <Box m={1}>
            <Button
              onClick={handleAddNewProfile}
              disabled={
                !(
                  searchFilters.manufacturer &&
                  searchFilters.category &&
                  searchFilters.model
                ) || !!treatedHoldingProfiles.length
              }
              variant="contained"
              color="secondary"
            >
              {t('AddNewHoldingButton')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

export default EquipmentHoldingProfileList;
